import React from 'react';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pickBy from 'lodash/pickBy';
import { getTotalCost } from './getTotalCost';
import {
    AmexPayment,
    BoletoPayment,
    DiscoverPayment,
    IdealPayment,
    MastercardPayment,
    OxxoPayment,
    PagofacilPayment,
    PaypalPayment,
    RapipagoPayment,
    SepaPayment,
    SofortPayment,
    VisaDebitPayment,
    VisaPayment,
} from '@eventbrite/eds-iconography';
import { formatMajorMoney } from '@eventbrite/intl';
import {
    getCountryAdjustedTicketPrice,
    getTicketsPriceRange,
} from '@eventbrite/event-tickets';

import { gettext } from '@eventbrite/i18n';
import {
    FREE_TEXT,
    TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
} from '../../../constants';
import { hasVariants } from '../../../utils/tickets';
import {
    getFirstVariantTicketWithPromoCode,
    isTicketWithPromoCode,
} from '../../../utils/promoCodes';
import { isTierVariantSoldOut } from './ticketUtils';

const _determineDisplayPrice = (ticket, currencyFormat) => {
    let displayPrice;

    // displayPrice for NL customers is totalCost (ticket cost + tax/fees)
    const cost = ticket.useAllInPrice ? ticket.totalCost : ticket.cost;

    if (cost && cost.value) {
        displayPrice = formatMajorMoney(
            cost.value,
            cost.currency,
            currencyFormat,
        );
    } else if (!cost && ticket.free) {
        displayPrice = FREE_TEXT;
    }

    return displayPrice;
};

const _determineTicketWithPromoCode = (ticket) => {
    let ticketWithCode;

    if (hasVariants(ticket)) {
        ticketWithCode = getFirstVariantTicketWithPromoCode(ticket.variants);
    } else if (isTicketWithPromoCode(ticket)) {
        ticketWithCode = ticket;
    }

    return ticketWithCode;
};

const _determinePrimaryPrice = (ticket, currencyFormat) => {
    const ticketWithCode = _determineTicketWithPromoCode(ticket);
    let primaryPrice;

    if (ticketWithCode && ticketWithCode.originalCost) {
        const { originalCost } = ticketWithCode;

        primaryPrice = formatMajorMoney(
            originalCost.value,
            originalCost.currency,
            currencyFormat,
        );
    }

    return primaryPrice;
};

const _determineRemainingTickets = (ticket, wasInWaitingRoom) => {
    let remaining;

    // if we were in a waiting room situation we have to hide remaining tickets
    // on the Ticket Selection, otherwise we can safely check ticket.displayFlags.showRemaining
    // https://jira.evbhome.com/browse/EB-52136
    if (!wasInWaitingRoom) {
        if (ticket.displayFlags && ticket.displayFlags.showRemaining) {
            remaining = ticket.quantityRemaining;
        }
    }

    return remaining;
};

const getFeeText = ({
    currencyFormat,
    fee,
    includeFee,
    isAppRebrandActive = false,
    prefix,
    tax,
    taxOnFee,
}) => {
    /*
    Should show ticket fee if includeFee is falsey, but response will
    sometimes not include it due to other circumstances. So need to ensure
    that key is present and false in order to display.
    */
    if (!fee || !fee.value || includeFee !== false) {
        return null;
    }

    const feeValue = taxOnFee ? fee.value + tax.value : fee.value;

    const feeAndTaxText = isAppRebrandActive
        ? gettext('fee & tax')
        : gettext('Fee & Tax');
    const feeText = isAppRebrandActive ? gettext('fee') : gettext('Fee');
    const feeName = taxOnFee ? feeAndTaxText : feeText;
    const displayFee = formatMajorMoney(feeValue, fee.currency, currencyFormat);

    return gettext('%(prefix)s%(displayFee)s %(feeName)s', {
        prefix,
        displayFee,
        feeName,
    });
};

const getTaxText = ({
    taxOnFee,
    tax,
    currencyFormat,
    hasGtsTax,
    eventTaxName,
    prefix,
    isAppRebrandActive = false,
}) => {
    if (taxOnFee || !tax || tax.value <= 0) {
        return null;
    }

    const displayTax = formatMajorMoney(
        tax.value,
        tax.currency,
        currencyFormat,
    );
    let taxName;

    if (hasGtsTax) {
        taxName = isAppRebrandActive
            ? gettext('sales tax')
            : gettext('Sales Tax');
    } else if (eventTaxName) {
        taxName = eventTaxName;
    } else {
        taxName = isAppRebrandActive ? gettext('tax') : gettext('Tax');
    }

    const taxText = gettext('%(prefix)s%(displayTax)s %(taxName)s', {
        prefix,
        displayTax,
        taxName,
    });

    return taxText;
};

const getShippingFeeText = ({
    displayShippingFee,
    prefix,
    currencyFormat,
    isAppRebrandActive = false,
}) => {
    if (!displayShippingFee || !displayShippingFee.value) {
        return null;
    }

    const shippingFeeName = isAppRebrandActive
        ? gettext('shipping fee')
        : gettext('Shipping fee');

    const shippingFeeValue = formatMajorMoney(
        displayShippingFee.value,
        displayShippingFee.currency,
        currencyFormat,
    );

    const shippingFeeText = gettext(
        '%(prefix)s%(shippingFeeValue)s %(shippingFeeName)s',
        {
            prefix,
            shippingFeeValue,
            shippingFeeName,
        },
    );

    return shippingFeeText;
};

/**
 * _determineFeeAndTax
 *
 * Helper function to determine what text should be displayed for the fee/tax of a ticket below the full ticket price.
 * - Will not show fee amount if there is none or it is being absorbed by the ticket price.
 * - Will not show tax amount if there is none.
 * - Will not show shipping fee amount if there is none.
 * - Will show "incl." at the beginning instead of "+" for tickets with useAllInPrice set to true (full ticket price is shown already).
 *
 * @param {Object} ticket
 * @returns {Array} display text to show
 **/
const _determineFeeAndTax = (
    {
        fee,
        tax,
        taxComponents = [],
        includeFee,
        eventTaxName,
        useAllInPrice,
        hasGtsTax,
        displayShippingFee,
    } = {},
    currencyFormat,
    isAppRebrandActive = false,
) => {
    const prefix = useAllInPrice ? 'incl. ' : '+';
    // We don't want to show taxes as a separate row if they are just taxes on the fees
    const taxOnFee = taxComponents.find(
        (component) =>
            component.base === TAX_COMPONENT_BASE_FEES_TAX_INCLUDABLE,
    );

    const feeText = getFeeText({
        currencyFormat,
        fee,
        includeFee,
        isAppRebrandActive,
        prefix,
        tax,
        taxOnFee,
    });

    const taxText = getTaxText({
        currencyFormat,
        eventTaxName,
        hasGtsTax,
        isAppRebrandActive,
        prefix,
        tax,
        taxOnFee,
    });

    const shippingFeeText = getShippingFeeText({
        currencyFormat,
        displayShippingFee,
        isAppRebrandActive,
        prefix,
    });

    return [feeText, taxText, shippingFeeText].filter((value) => !!value);
};

export const determinePriceInformation = ({
    currencyFormat,
    isAppRebrandActive,
    ticket,
    wasInWaitingRoom,
}) => ({
    displayPrice: _determineDisplayPrice(ticket, currencyFormat),
    quantityRemaining: _determineRemainingTickets(ticket, wasInWaitingRoom),
    feeAndTax: _determineFeeAndTax(ticket, currencyFormat, isAppRebrandActive),
    primaryPrice: _determinePrimaryPrice(ticket, currencyFormat),
});

/**
 * getDisplayCostFromValue
 *
 * @param {number} value The pre-formatted cost value
 * @returns {string} Formatted currency string, or 'Free'
 */
export const getDisplayCostFromValue = (value, currency, currencyFormat) =>
    value === 0 ? FREE_TEXT : formatMajorMoney(value, currency, currencyFormat);

/**
 * getTotalDisplayCost
 *
 * Formats the total cost of all selected tickets according to currency type and
 * returns the formatted string. If total cost is zero (0), the display will
 * return the localized equivalent of the string "Free".
 *
 * eg. (tickets, currency) => '$23.42'
 *
 * @param {Object} tickets
 * @param {string} currency
 * @param {string} currencyFormat The currency format is used to determine thousands & decimal separators, and position the currency symbol
 **/

export const getTotalDisplayCost = (
    tickets,
    currency,
    currencyFormat,
    shippingFee = 0,
) =>
    getDisplayCostFromValue(
        getTotalCost(tickets) + shippingFee,
        currency,
        currencyFormat,
    );

/**
 * _getValidKeys
 *
 * Given the paymentOptions object returned from the backend,
 * returns an array of only the keys whose values are truthy.
 * Excluding 'hasPaymentOptions'
 *
 * @param {Object} paymentOptions
 **/
const _getValidKeys = (paymentOptions) =>
    Object.keys(
        pickBy(
            omit(paymentOptions, 'hasPaymentOptions'),
            (value) => value === true,
        ),
    );

// Payment options returned from the event service mapped to the corresponding
// icon type.
const PAYMENT_OPTION_ICON_MAP = {
    shouldAcceptAmexForPayment: <AmexPayment />,
    shouldAcceptBoletoBancario: <BoletoPayment />,
    shouldAcceptDiscoverForPayment: <DiscoverPayment />,
    shouldAcceptIdeal: <IdealPayment />,
    shouldAcceptMastercardForPayment: <MastercardPayment />,
    shouldAcceptOxxo: <OxxoPayment />,
    shouldAcceptPagofacil: <PagofacilPayment />,
    shouldAcceptRapipago: <RapipagoPayment />,
    shouldAcceptSepaDirectDebit: <SepaPayment />,
    shouldAcceptSofort: <SofortPayment />,
    shouldAcceptVisaForPayment: <VisaPayment />,
    shouldAcceptVisaDebitForPayment: <VisaDebitPayment />,
    shouldShowPaypalPaymentMethod: <PaypalPayment />,
};

/**
 * _getValidPaymentIconTypes
 *
 * Given an array of payment options, return the corresponding payment icon types.
 *
 * @param {Array} validKeys
 **/
const _getValidPaymentIconTypes = (validKeys) =>
    validKeys.map((paymentOption) => PAYMENT_OPTION_ICON_MAP[paymentOption]);

/**
 * getValidPaymentOptions
 *
 * Receives the paymentOptions object as defined by Event Service Response, and
 * returns an array of valid payment options formatted to be passed directly into
 * the <TicketPage />
 *
 * @param {Object} paymentOptions
 **/
export const getValidPaymentOptions = (paymentOptions) => {
    let validPaymentIconTypes;

    if (paymentOptions.hasPaymentOptions) {
        validPaymentIconTypes = _getValidPaymentIconTypes(
            _getValidKeys(paymentOptions),
        );
    }

    return validPaymentIconTypes;
};

/**
 * getVariantRangeCostForDisplay
 *
 * Display a price range given the variants, there are three common examples:
 * - If all variants are priced, a normal range is returned
 * `$50.00-$100`
 *
 * - If all variants are free, a simple "Free" text is returned
 * - If at least one variant is free, a range is returned starting from '$0.00'
 * `$0.00-$20.00`
 *
 * @param {Array} variants list of variant objects attached to tiered ticket object
 * @param {String} currencyFormat
 *
 * @return {String} the price range for the given list of variants
 */
export const getVariantRangeCostForDisplay = (variants, currencyFormat) => {
    // get variants actually representing tickets, either it has `cost` field or `free` is true
    const ticketVariants = variants.filter(({ cost, free }) => cost || free);

    const paidVariants = ticketVariants.filter(({ free }) => !free);

    if (paidVariants.length === 0) {
        return FREE_TEXT;
    }

    const variantsNotSoldOut = ticketVariants.filter(
        (variant) => !isTierVariantSoldOut(variant),
    );
    let variantsForPriceRange;

    if (isEmpty(variantsNotSoldOut)) {
        variantsForPriceRange = ticketVariants;
    } else {
        variantsForPriceRange = variantsNotSoldOut;
    }

    const transformedVariants = variantsForPriceRange.map((variant) =>
        variant.free
            ? {
                  ...variant,
                  cost: {
                      value: 0,
                      majorValue: 0,
                  },
              }
            : variant,
    );

    return getTicketsPriceRange(transformedVariants, currencyFormat);
};

/**
 * getVariantCostForDisplay
 *
 * Returns a formatted price string for a given variant.
 *
 * @param {Object} variant the `variant` object which represents a tier
 * @param {String} currencyFormat
 * @returns {String} actualy single price for the given tier variant.
 */

export const getVariantCostForDisplay = (variant, currencyFormat) => {
    const { value = 0, currency } = getCountryAdjustedTicketPrice(variant);

    return value === 0
        ? FREE_TEXT
        : `${formatMajorMoney(value, currency, currencyFormat)}`;
};

/**
 * Returns a formatted string using the `originalCost` object (type: Currency Cost) that is returned
 * on variants after a promo code is applied. For more details about return value see:
 * https://github.com/eventbrite/js-utils/tree/master/src/intl#formatmajormoney
 *
 * @param {Object} variant The `variant` object of type Tier
 * @param {String} currencyFormat
 * @returns {String}
 */
export const getVariantDiscountCostForDisplay = (variant, currencyFormat) => {
    if (!variant || !variant.originalCost) {
        return '';
    }

    const {
        originalCost: { value, currency },
    } = variant;

    return formatMajorMoney(value, currency, currencyFormat);
};
